import { Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AudioApi } from '../../api/AudioApi';
import { DialogPromptSelectAudioSpeed } from '../../api/DialogPromptApi';
import { SentenceApi } from '../../api/SentenceApi';
import { ChineseTeacher, IUser, UserApi } from '../../api/UserApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherIntroductionFemale, TeacherIntroductionFemale2, TeacherIntroductionMale, getAudioByText } from './DialogPromptComponent';

interface DialogPromptSelectAudioSpeedComponentProps {
    dialogPrompt: DialogPromptSelectAudioSpeed;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptSelectAudioSpeedComponent: React.FC<DialogPromptSelectAudioSpeedComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const { playAudio } = useAudioPlayer();

    const [teacherSentenceId, setTeacherSentenceId] = React.useState<number | null>(null);

    const [response, setResponse] = React.useState<string | null>(null);

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [chatBubbleDone2, setChatBubbleDone2] = React.useState(false);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [isAudioSpeedFast, setAudioSpeedFast] = React.useState<boolean | null>(null);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)

        const teacher = localStorage.getItem('teacher');
        let message = null;
        if (teacher == ChineseTeacher.Male) {
            message = TeacherIntroductionMale
        } else if (teacher == ChineseTeacher.Female) {
            message = TeacherIntroductionFemale
        } else if (teacher == ChineseTeacher.Female2) {
            message = TeacherIntroductionFemale2
        } else {
            throw Error("unknown selected teacher name in local storage " + teacher);
        }
        SentenceApi.searchSentenceByText(message)
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting teacher audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(sentenceList => {
                const shortestSentence = sentenceList
                    .filter(s => s.text && s.text.trim().length > 0)
                    .reduce((shortest, current) =>
                        current.text.length < shortest.text.length ? current : shortest
                    );
                setTeacherSentenceId(shortestSentence.id)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [clearFooterList, dialogPrompt.prompt, history, location, setErrorMessage]);

    const playTeacherAudio = React.useCallback(() => {
        if (teacherSentenceId) {
            AudioApi.getTeacherSentence(teacherSentenceId)
                .then(response => {
                    // check if the response was successful
                    if (response.status >= 300) {
                        throw new Error('Getting teacher audio was not ok statuscode=' + response.status + " " + response.statusText);
                    }
                    return response.data;
                })
                .then(audioData => {
                    playAudio(audioData)
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })

        }
    }, [teacherSentenceId, playAudio, setErrorMessage, history, location]);

    const updateDbSelectAudioSpeed = React.useCallback((audioSpeedFast: boolean) => {
        const updatedUser: IUser = {
            audio_speed_fast: audioSpeedFast,
        };
        // Call the update function
        UserApi.update(updatedUser)
            .then(() => {
                // Handle the response
                // console.log('User updated:', response.data);
                playTeacherAudio()
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [playTeacherAudio, setErrorMessage, history, location]);

    const handleYesClick = React.useCallback(() => {
        setAudioSpeedFast(true)
        updateDbSelectAudioSpeed(true);
    }, [setAudioSpeedFast, updateDbSelectAudioSpeed]);

    const handleNoClick = React.useCallback(() => {
        setAudioSpeedFast(false)
        updateDbSelectAudioSpeed(false);
    }, [setAudioSpeedFast, updateDbSelectAudioSpeed]);

    const handleSubmitClick = React.useCallback(() => {
        if (isAudioSpeedFast === null) return; // do nothing

        if (isAudioSpeedFast) {
            setResponse(dialogPrompt.yes_response);
        } else {
            setResponse(dialogPrompt.no_response);
        }
    }, [isAudioSpeedFast, setResponse, dialogPrompt.yes_response, dialogPrompt.no_response]);

    React.useEffect(() => {
        if (chatBubbleDone) {
            setActionList([
                <Stack direction="row" spacing={2} alignItems="center" key="selection-buttons">
                    <Button
                        variant='contained'
                        onClick={handleYesClick}
                        sx={{
                            backgroundColor: '#F2F7FFCC', // 80% transparent
                            color: '#000',
                            border: isAudioSpeedFast === true ? '2px solid #1976d2' : '2px solid transparent',
                            '&:hover': {
                                backgroundColor: '#F2F7FFFF', // full opacity
                            },
                            boxShadow: 'none',
                        }}
                    >
                        {dialogPrompt.yes_button_text}
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleNoClick}
                        sx={{
                            backgroundColor: '#F2F7FFCC', // 80% transparent
                            color: '#000',
                            border: isAudioSpeedFast === false ? '2px solid #1976d2' : '2px solid transparent',
                            '&:hover': {
                                backgroundColor: '#F2F7FFFF', // full opacity
                            },
                            boxShadow: 'none',
                        }}
                    >
                        {dialogPrompt.no_button_text}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitClick}
                        disabled={isAudioSpeedFast === null}
                    >
                        Continue
                    </Button>
                </Stack>
            ])
        }
    }, [chatBubbleDone, isAudioSpeedFast, dialogPrompt.no_button_text, dialogPrompt.yes_button_text, handleNoClick, handleSubmitClick, handleYesClick, setActionList]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    })

    React.useEffect(() => {
        if (chatBubbleDone2) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleDone2, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {response != null && (
                <ChatBubble
                    isTeacher={!dialogPrompt.is_teacher}
                    message={response}
                    messageAudio={null}  // no audio for speech by students
                    showAvatar={showAvatar}
                    setChatBubbleDone={setChatBubbleDone2}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            )}
        </Stack>
    );
};

export default DialogPromptSelectAudioSpeedComponent;
